import React, {
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Grid,
  styled,
} from '@mui/material';
import {
  Form,
  RecordContextProvider,
  SelectInput,
  required,
  useGetList,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Currencies } from '../../constants/currencies';
import Params from './Params';
import Summary from './Summary';
import { requestTypes } from '../../constants/requestTypes';
import entityTypes, { EntityTypes } from '../../constants/entityTypes';
import SubmitOnChange from '../utils/SubmitOnChange';
import { getEntityDisplayName } from '../../customFields/EntityDisplayNameField';
import SnapshotButton from './SnapshotButton';
import hasAccess from '../../utilities/hasAccess';
import actions, { Actions } from '../../constants/actions';
import DateRange from '../utils/DateRange';
import { relatedEntitiesTypes } from '../../constants/productTypes';

const StyledForm = styled(Form)({
  width: '100%',
});
type Record = {
  id: string;
  name?: string;
  accountName?: string;
  firstName?: string;
  lastName?: string;
  productDescription?: string;
  currencies?: Currencies[];
};

const TransactionFilterForm = ({
  params,
  filter,
  entityType,
  setParams,
}: {
  params: Params;
  filter: {
    from: string;
    to: string;
    currency: string;
    entityId: string;
    entityType: EntityTypes;
    requestType: typeof requestTypes.TRANSACTION;
  };
  entityType: EntityTypes;
  setParams: React.Dispatch<React.SetStateAction<Params>>;
}) => {
  const record = useRecordContext<Record>();
  const { permissions } = usePermissions<Actions[]>();

  let shouldFetchAccounts = false;
  if (record && (entityType === entityTypes.BUSINESS
    || entityType === entityTypes.INDIVIDUAL)) shouldFetchAccounts = true;

  let shouldFetchProducts = false;
  if (record && (entityType === entityTypes.BUSINESS
    || entityType === entityTypes.INDIVIDUAL
    || entityType === entityTypes.ACCOUNT)) shouldFetchProducts = true;

  const { data: accountData, isLoading: isLoadingAccount } = useGetList<{
    id: string;
    accountName: string;
  }>(
    'account-entities',
    {
      filter: { 'connectedEntities.id': record!.id },
      pagination: { page: 1, perPage: 300 },
    },
    { enabled: shouldFetchAccounts },
  );

  const { data: productData, isLoading: isLoadingProduct } = useGetList<{
    id: string;
    productDescription?: string;
    productType?: string;
  }>(
    'products',
    {
      filter: {
        'parties.id': record!.id,
        productType: relatedEntitiesTypes,
      },
      pagination: { page: 1, perPage: 300 },
    },
    {
      enabled: shouldFetchProducts,
    },
  );

  const totalChoices = useMemo(() => {
    let accountChoices;
    if (shouldFetchAccounts && !isLoadingAccount) {
      accountChoices = accountData?.map((account) => ({
        id: account.id,
        name: `${entityTypes.ACCOUNT}: ${getEntityDisplayName({ record: account, type: entityTypes.ACCOUNT })}`,
      }));
    }
    let productChoices;
    if (shouldFetchProducts && !isLoadingProduct) {
      productChoices = productData?.map((product) => ({
        id: product.id,
        name: `${entityTypes.PRODUCT}: ${getEntityDisplayName({ record: product, type: entityTypes.PRODUCT })}`,
      }));
    }

    return [
      ...(accountChoices || []),
      ...(productChoices || []),
      ...(record ? [{ id: record.id, name: `${entityType}: ${getEntityDisplayName({ record, type: entityType })}` }] : []),
    ];
  }, [
    accountData,
    entityType,
    isLoadingAccount,
    isLoadingProduct,
    productData,
    record,
    shouldFetchAccounts,
    shouldFetchProducts,
  ]);

  const onSubmit = useCallback((newParams: Params) => {
    let selectedEntityType = entityType;
    if (accountData?.find(({ id }) => id === newParams.entityId)) {
      selectedEntityType = entityTypes.ACCOUNT;
    } else if (productData?.find(({ id }) => id === newParams.entityId)) {
      selectedEntityType = entityTypes.PRODUCT;
    }

    setParams((previousParams) => ({
      ...previousParams,
      ...newParams,
      selectedRow: newParams.selectedRow === 'null' ? undefined : newParams.selectedRow,
      entityType: selectedEntityType,
    }));
  }, [accountData, entityType, productData, setParams]);

  return (
    <StyledForm record={params}>
      <RecordContextProvider value={record}>
        <Grid container spacing={4}>
          <Grid size={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
              <Box display="flex" gap={4} flexWrap="wrap" alignItems="center">
                <SelectInput
                  label="Currency"
                  source="currency"
                  choices={record?.currencies?.map((currency) => ({
                    id: currency,
                    name: currency,
                  }))}
                  validate={required()}
                  helperText={false}
                />
                {!isLoadingAccount && !isLoadingProduct && totalChoices.length > 0 && (
                  <SelectInput
                    label="Entity"
                    source="entityId"
                    choices={totalChoices}
                    validate={required()}
                    helperText={false}
                  />
                )}
                <DateRange setParams={setParams} params={params} />
                <SubmitOnChange onSubmit={onSubmit} />
              </Box>
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={4}>
                {hasAccess(permissions, actions.CASE_TRANSACTION_SNAPSHOT_CREATE)
                  && (<SnapshotButton filter={filter} params={params} />)}
              </Box>
            </Box>
          </Grid>
          <Grid size={12}>
            <Summary params={params} filter={filter} />
          </Grid>
        </Grid>
      </RecordContextProvider>
    </StyledForm>
  );
};

export default TransactionFilterForm;
